/**
 * MBA Lab — the Team Retrospective Facilitator's prompt content. Pure data,
 * no functions, matching listening-prompts.js / readiness-dimensions.js.
 *
 * Every string here is lifted verbatim from this site's own already-published
 * article, Content/Blog/mba-team-retrospectives.md - this tool operationalizes
 * that one 30-minute format rather than inventing a second, competing one.
 * That is also why there is no format picker and no timer anywhere in this
 * tool: the article prescribes exactly one format ("Run this after each major
 * deliverable. Timebox hard.") and the timebox card below is a display-only
 * reminder of it, not a clock.
 *
 * Views/Tools/TeamRetrospectiveFacilitator.cshtml hand-duplicates these
 * strings into a C# data literal so the prompts are in the very first paint
 * with no JS - src/tools/tests/razor-duplication-sync.test.js asserts the two
 * copies stay identical. Change a string here and that test tells you which
 * Razor line to change with it.
 */

/**
 * The three silent-writing prompts. The article's wording is "Everyone writes
 * answers to exactly three prompts" - three is the whole point, so this list
 * is fixed-length by intent, not by accident.
 */
export const CAPTURE_PROMPTS = Object.freeze([
  { key: 'keepDoing', label: 'What should we keep doing?' },
  { key: 'slowedUs', label: 'What slowed us down?' },
  { key: 'oneChange', label: 'What one change would help most next time?' },
]);

/**
 * The 5' / 15' / 10' structure, rendered as a STATIC card. `minutes` is here
 * to print "5 min" next to each phase, never to drive a countdown - a running
 * clock on a student retro turns a 30-minute conversation into a performance
 * against a timer, and the article's own advice is that the facilitator
 * timeboxes it, not the software.
 */
export const TIMEBOXES = Object.freeze([
  {
    key: 'silent',
    minutes: 5,
    label: 'Five minutes: silent writing',
    detail: 'Everyone writes answers to exactly three prompts.',
  },
  {
    key: 'cluster',
    minutes: 15,
    label: 'Fifteen minutes: read and cluster',
    detail: 'Two people independently naming the same friction point is a signal, not a coincidence.',
  },
  {
    key: 'commit',
    minutes: 10,
    label: 'Ten minutes: commit to two changes',
    detail: 'Two is the limit. Teams that leave with five commitments keep zero.',
  },
]);

/**
 * The article's "prompts that get past politeness" - swap-ins for a team stuck
 * at what it calls the politeness ceiling. Surfaced as a reference card the
 * facilitator can read out, not as extra capture fields: adding three more
 * textareas would quietly turn a three-prompt format into a six-prompt one.
 */
export const SHARPER_PROMPTS = Object.freeze([
  { key: 'hours', text: 'Where did we lose the most hours for the least output?' },
  { key: 'unseen', text: 'What did we each spend time on that the grade never saw?' },
  { key: 'halfMeeting', text: 'If we had to cut our meeting time in half, what would we stop doing?' },
]);

/** Why the sharper prompts are safe to ask in a cohort you can't leave. */
export const SHARPER_PROMPTS_NOTE =
  'These aim at the work, not the person, which is what makes honesty survivable in a cohort you\'ll see every day.';

/** Why the first five minutes are silent - the one detail the article says matters most. */
export const SILENT_WRITING_NOTE =
  'Use silent writing first: everyone writes observations for five minutes before anyone speaks. It equalizes airtime before discussion starts.';

/** What the reviewMoment field is actually for: the loop that makes a retro compound. */
export const REVIEW_MOMENT_NOTE =
  'Put the two commitments at the top of the next meeting agenda and spend sixty seconds on them: did we do it, or not?';
